<template>
  <div class="row">
    <div class="col-12">
      <filters v-model="filter" :filter-config="filterConfig" />
    </div>
    <div class="col-12">
      <div class="row">
        <div class="col-12 justify-content-end">
          <div class="action-buttons">
            <b-button variant="primary" size="sm" @click="showNewRow = !showNewRow"> <BIconPlus />{{ 'New User' | i18n }} </b-button>
            <b-button :disabled="!selectedRow || selectedRow.editMode" variant="primary" size="sm" @click="toggleEditMode(selectedRow)">
              {{ 'edit' | i18n }}
            </b-button>
            <b-button :disabled="!selectedRows.length" variant="outline-primary" size="sm" @click="deleteUsers">
              - {{ 'Remove User' | i18n }}
            </b-button>
            <b-dropdown variant="outline-primary" right class="actions-dropdown" :disabled="!selectedRow" size="sm">
              <b-dropdown-item v-b-modal.resetPasswordModal>
                {{ 'change_password' | i18n }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <b-table
            id="users-table"
            ref="table"
            :fields="fields"
            :items="getPage"
            :filter="filter"
            :sticky-header="true"
            :responsive="true"
            :busy="isLoading"
            :per-page="perPage"
            :current-page="currentPage"
            :sort-by="'email'"
            :label-sort-asc="sortAscTitle"
            :label-sort-desc="sortDescTitle"
            :empty-text="noTableDataTitle"
            :empty-filtered-text="noTableDataTitle"
            fixed
            show-empty
            @refreshed="resetSelectedRow"
          >
            <template #head(check)>
              <b-form-checkbox id="checkbox__all" v-model="allSelected" :checked="allSelected" name="checkbox__all" @change="toggleSelectAll" />
            </template>
            <template #cell(check)="row">
              <b-form-checkbox
                :id="'checkbox__' + row.item.id"
                v-model="row.item.isSelected"
                :checked="row.item.isSelected || allSelected"
                :name="'checkbox__' + row.item.id"
                @change="toggleSelectRow(row.item)"
              />
            </template>
            <template #cell(username)="row">
              <span v-if="!row.item.editMode">{{ row.item.username }}</span>
              <b-form-group v-if="row.item.editMode" id="user-name">
                <b-form-input id="user-name-input" v-model="editedRow.username" :placeholder="'name' | i18n" size="sm" trim />
              </b-form-group>
            </template>
            <template #cell(email)="row">
              <span v-if="!row.item.editMode">{{ row.item.email }}</span>
              <b-form-group v-if="row.item.editMode" id="user-email">
                <b-form-input id="user-email-input" v-model="editedRow.email" :placeholder="'email' | i18n" size="sm" trim />
              </b-form-group>
            </template>
            <template #cell(role)="row">
              <span v-if="!row.item.editMode">{{ row.item.role }}</span>
              <b-form-group v-if="row.item.editMode" id="user-role">
                <b-form-select v-model="editedRow.role" :options="rolesOptions" size="sm" />
              </b-form-group>
            </template>
            <template #cell(createdAt)="row">
              <span v-if="!row.item.editMode">{{ row.item.createdAt | formatDate('DD.MM.YY HH:mm') }}</span>
            </template>
            <template #cell(pushToken)="row">
              {{ row.item.pushToken ? `${row.item.pushToken.slice(0, 10)}...` : '' }}
            </template>
            <template #cell(actions)="row">
              <b-button
                v-if="row.item.editMode"
                v-b-tooltip.hover
                :disabled="!isEditedRowValid"
                variant="primary"
                size="sm"
                :title="'save' | i18n"
                @click="updateRow(row.item)"
              >
                <BIconCheck />
              </b-button>
              <b-button
                v-if="row.item.editMode"
                v-b-tooltip.hover
                class="btn-new-row"
                variant="outline-primary"
                size="sm"
                :title="'abort' | i18n"
                @click="toggleEditMode(row.item)"
              >
                <BIconX />
              </b-button>
            </template>
            <template v-if="showNewRow" #top-row>
              <b-td />
              <b-td>
                <b-form-group id="new-user-name">
                  <b-form-input id="new-user-name-input" v-model="newUser.username" :placeholder="'name' | i18n" size="sm" trim />
                </b-form-group>
              </b-td>
              <b-td>
                <b-form-group id="new-user-email">
                  <b-form-input id="new-user-email-input" v-model="newUser.email" :placeholder="'email' | i18n" size="sm" trim />
                </b-form-group>
              </b-td>
              <b-td>
                <b-form-group id="new-user-role">
                  <b-form-select v-model="newUser.role" :options="rolesOptions" size="sm" />
                </b-form-group>
              </b-td>
              <b-td>
                <password v-model="newUser.password" :placeholder="'password' | i18n" />
              </b-td>
              <b-td class="actions">
                <b-button
                  v-b-tooltip.hover
                  variant="primary"
                  size="sm"
                  class="btn-create-user"
                  :disabled="!isNewUserValid"
                  :title="'save' | i18n"
                  @click="createUser"
                >
                  <BIconCheck />
                </b-button>
              </b-td>
            </template>
            <template #table-busy>
              <div class="text-center">
                <b-spinner class="align-middle" />
                <strong class="ml-1">{{ 'loading' | i18n }}...</strong>
              </div>
            </template>
          </b-table>
          <b-pagination v-model="currentPage" class="table-pagination" :total-rows="totalCount" :per-page="perPage" size="sm" />
        </div>
      </div>
    </div>
    <b-modal
      v-if="selectedRow"
      id="resetPasswordModal"
      ref="resetPasswordModal"
      :title="'password_update' | i18n"
      :ok-title="'save' | i18n"
      :cancel-title="'abort' | i18n"
      ok-variant="primary"
      cancel-variant="outline-primary"
      button-size="sm"
      @ok="resetUserPassword"
    >
      <password v-model="selectedRow.newPassword" :placeholder="'new_password' | i18n" form-group-id="reset-user-password" />
    </b-modal>
  </div>
</template>

<script>
import { BTable, BTd, BIconCheck, BIconX, BIconPlus } from 'bootstrap-vue';
import { NotificationMixin, UserMixin, DeleteRowMixin, AuthMixin } from '../../mixins';
import Filters from '../table/filters';
import { SORT_ASC_TITLE, SORT_DESC_TITLE, ROLES, NO_TABLE_DATA_TITLE } from '../../../../../constants';
import Password from '../inputs/password';

export default {
  name: 'UserManagement',
  components: {
    Password,
    Filters,
    BTable,
    BTd,
    BIconCheck,
    BIconX,
    BIconPlus
  },
  mixins: [NotificationMixin, UserMixin, DeleteRowMixin, AuthMixin],
  data: function() {
    return {
      users: [],
      fields: [
        { key: 'check', label: '', sortable: false, thClass: 'width-5' },
        { key: 'username', label: this.$translate('name'), sortable: true, thClass: 'width-20', tdClass: 'width-20' },
        { key: 'email', label: this.$translate('email'), sortable: true, thClass: 'width-30', tdClass: 'width-30' },
        { key: 'role', label: this.$translate('user_role'), sortable: true },
        { key: 'pushToken', label: this.$translate('push_identifier'), sortable: true },
        { key: 'createdAt', label: this.$translate('created_date'), sortable: true, thClass: 'width-15', tdClass: 'width-15' },
        { key: 'actions', label: '', sortable: false, thClass: ['width-13', 'actions'], tdClass: 'actions' }
      ],
      filter: {
        email: null,
        username: null,
        role: null
      },
      filterConfig: {
        mainFilters: []
      },
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      isLoading: false,
      sortAscTitle: this.$translate(SORT_ASC_TITLE),
      sortDescTitle: this.$translate(SORT_DESC_TITLE),
      noTableDataTitle: this.$translate(NO_TABLE_DATA_TITLE),
      allSelected: false,
      selectedRow: null,
      editedRow: null,
      showNewRow: false,
      newUser: {
        email: null,
        username: null,
        password: null,
        role: null
      },
      roles: ROLES,
      rolesOptions: [{ value: null, text: this.$translate('user_role') }, ...Object.values(ROLES)],
      isPasswordVisible: false
    };
  },
  computed: {
    selectedRows: function() {
      return this.users.filter(r => r.isSelected);
    },
    isNewUserValid: function() {
      return !!this.newUser.username && !!this.newUser.email && !!this.newUser.password && !!this.newUser.role;
    },
    isEditedRowValid: function() {
      return this.editedRow && this.editedRow.username && this.editedRow.role && this.editedRow.email;
    }
  },
  created() {
    this.filterConfig.mainFilters = [
      { key: 'email', label: 'email', inputType: 'text' },
      { key: 'username', label: 'name', inputType: 'text' },
      { key: 'role', label: 'user_role', inputType: 'select', filterOptions: this.rolesOptions }
    ];
  },
  methods: {
    async getPage(ctx) {
      try {
        const requestFilters = {};
        Object.keys(this.filter)
          .filter(key => !!this.filter[key])
          .forEach(key => {
            requestFilters[key] = this.filter[key];
          });
        const requestParams = {
          ...requestFilters,
          page: ctx.currentPage,
          limit: ctx.perPage
        };

        if (ctx.sortBy) {
          requestParams.sortBy = ctx.sortBy;
          requestParams.sortOrder = ctx.sortDesc ? 'desc' : 'asc';
        }

        this.isLoading = true;
        const response = await this.userService.getUsers(requestParams);
        this.totalCount = response.totalCount;
        this.isLoading = false;
        this.users = response.data.map(user => ({
          ...user,
          isSelected: false,
          editMode: false
        }));
        return this.users;
      } catch (err) {
        this.showNotification({ type: 'error', title: this.$translate('user_administration'), message: err.message });
      }
    },
    toggleSelectAll() {
      this.allSelected = !this.allSelected;
      this.users.forEach(u => (u.isSelected = this.allSelected));
      this.selectedRow = null;
    },
    toggleSelectRow(row) {
      const isSelected = !row.isSelected;
      const prevSelectedRows = this.selectedRows;
      const newSelectedRows = isSelected ? prevSelectedRows.concat([row]) : prevSelectedRows.filter(p => p.id !== row.id);

      if (newSelectedRows.length === 1) {
        this.selectedRow = isSelected ? row : newSelectedRows[0];
        this.allSelected = false;
      } else {
        this.selectedRow = null;
        this.editedRow = null;
      }
    },
    async deleteUsers() {
      const rowIds = this.selectedRows.map(p => p.id);
      await this.deleteSelectedRows(
        {
          message: this.$translate('user_delete_confirmation_message'),
          rowDisplayValues: this.selectedRows.map(r => r.username)
        },
        async () => {
          await this.userService.deleteUsers(rowIds);
          await this.$refs.table.refresh();
        }
      );
    },
    async createUser() {
      if (!this.isNewUserValid) {
        return this.showNotification({ type: 'error', title: this.$translate('user_creation'), message: this.$translate('invalid_user_data') });
      }

      try {
        await this.userService.createUser(this.newUser);
        this.showNotification({ type: 'success', title: this.$translate('user_creation'), message: this.$translate('user_create_success_message') });
        this.resetNewUser();
        this.showNewRow = false;
        await this.$refs.table.refresh();
      } catch (err) {
        this.showNotification({ type: 'error', title: this.$translate('user_creation'), message: err.message });
      }
    },
    resetNewUser() {
      Object.keys(this.newUser).forEach(key => (this.newUser[key] = null));
    },
    resetSelectedRow() {
      this.selectedRow = null;
    },
    toggleEditMode(item) {
      if (!item.editMode) {
        this.users.forEach(item => (item.editMode = false));
      }
      item.editMode = !item.editMode;
      this.editedRow = { ...item };
    },
    async updateRow(user) {
      try {
        await this.userService.updateUser(user.id, {
          username: this.editedRow.username,
          email: this.editedRow.email,
          role: this.editedRow.role
        });
        user.editMode = false;
        this.toggleSelectRow(this.selectedRow);
        this.$refs.table.refresh();
        this.showNotification({ type: 'success', title: this.$translate('user_update'), message: this.$translate('user_update_success_message') });
      } catch (err) {
        this.showNotification({ type: 'error', title: this.$translate('user_update'), message: err.message });
      }
    },
    async resetUserPassword(event) {
      event.preventDefault();
      try {
        await this.userService.resetUserPassword(this.selectedRow.id, {
          newPassword: this.selectedRow.newPassword
        });
        this.$refs.resetPasswordModal.hide();
        this.showNotification({
          type: 'success',
          title: this.$translate('password_update'),
          message: this.$translate('password_update_success_message')
        });
      } catch (err) {
        this.showNotification({ type: 'error', title: this.$translate('password_update'), message: err.message });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.b-table-sticky-header {
  max-height: calc(100vh - 455px);
  min-height: calc(100vh - 455px);
}

::v-deep   .table {
  min-width: 970px;
  white-space: normal;
  text-overflow: ellipsis;
  overflow: hidden;

  td,
  th {
    padding: 8px 14px !important;
  }

  th {
    &.width-30 {
      width: 30%;
    }

    &.width-20 {
      width: 20%;
    }

    &.width-15 {
      width: 15%;
    }

    &.width-5 {
      width: 5%;
    }
  }

  th.actions,
  td.actions {
    text-align: right;
  }

  td.actions button:first-child {
    margin-right: 5px;
  }

  th.actions {
    vertical-align: bottom;
  }
}

.btn-create-user {
  margin-right: 0 !important;
}
</style>
